import { Container, Typography } from '@cleartrip/ct-design-components'
import CheckboxItem from './checkboxItem'
import TimingCards from './TimingCards'
import { FilterData as FilterDataType, PossibleFilterTypes } from '@/types/srp'
import { DEPARTURE_TIME_MAPPING_OBJ } from '@/constants/Srp'
import { useAppSelector } from '@/redux/store/hooks'
import { getSrpSearchData } from '@/redux/slices/srpSlice'

type PossibleTimeValues = keyof typeof DEPARTURE_TIME_MAPPING_OBJ

type timingFilterProps = {
  departureTime: {
    options: PossibleTimeValues[]
    selectedValues: PossibleTimeValues[]
    airportCode: string
  }
  onFilterChange: (
    type: PossibleFilterTypes,
    filtersData: FilterDataType,
    index: number,
    legKey?: 'onwardLeg' | 'returnLeg'
  ) => void
  index: number
  filterType: string
  fromCode?: string
  toCode?: string
  /** When SRP airport metadata is unavailable (e.g. flight tracking). */
  fromCityLabel?: string
  toCityLabel?: string
  isRevamp?: boolean
}

const TimingFilter = ({
  departureTime,
  onFilterChange,
  index,
  filterType,
  fromCode,
  toCode,
  fromCityLabel,
  toCityLabel,
  isRevamp = false,
}: timingFilterProps) => {
  const { options, selectedValues = [], airportCode } = departureTime || {}
  const { searchData } = useAppSelector(getSrpSearchData)
  const { airportDetail = {} } = searchData?.metaData || {}

  const resolveCity = (label?: string, code?: string) =>
    label ?? (code ? airportDetail[airportCode]?.city || airportDetail[code]?.city || '' : '')
  const fromCity = resolveCity(fromCityLabel, fromCode)
  const toCity = resolveCity(toCityLabel, toCode)

  const onDepartureItemClick = (option: PossibleTimeValues) => {
    let updatedDepartureTimeSelectedValues: PossibleTimeValues[]
    if (selectedValues.includes(option)) {
      updatedDepartureTimeSelectedValues = selectedValues.filter(value => value !== option)
    } else {
      updatedDepartureTimeSelectedValues = [...selectedValues, option]
    }
    onFilterChange(filterType, updatedDepartureTimeSelectedValues, index)
  }

  if (isRevamp) {
    let heading = ''
    if (fromCode && fromCity) heading = `Take off from ${fromCity}`
    else if (toCode && toCity) heading = `Landing in ${toCity}`

    return (
      <TimingCards
        heading={heading}
        options={options}
        selectedValues={selectedValues}
        onToggle={onDepartureItemClick}
      />
    )
  }

  return (
    <>
      {fromCode && fromCity && (
        <Typography variant='B2' className='c-pointer mb-3'>{`Taking off from ${fromCity}`}</Typography>
      )}
      {toCode && toCity && <Typography variant='B2' className='c-pointer my-3'>{`Landing in ${toCity}`}</Typography>}
      <Container className='pb-3'>
        {options?.map(option => {
          const checked = selectedValues.includes(option)
          const { label, subLabel } = DEPARTURE_TIME_MAPPING_OBJ[option]
          const labelJsx = (
            <Container className='flex flex-between w-100p'>
              <Typography variant='P2' className='c-pointer'>
                {label}
              </Typography>
              <Typography variant='P2' className=' c-pointer' color='subheading'>
                {subLabel}
              </Typography>
            </Container>
          )
          return (
            <CheckboxItem
              key={option}
              label={labelJsx}
              checked={checked}
              onClick={() => onDepartureItemClick(option)}
              wrapperClassName='py-2 checkbox-hover-border'
              rootClassName='w-100p'
            />
          )
        })}
      </Container>
    </>
  )
}

export default TimingFilter
